import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import AdvanceSelectLabel from '../../advance-select/advance-select-label';
import DatepickerBox from '../../datepicker-box/datepicker-box';
import {
  BorderedBoxIcon,
  CloseIcon,
  NextIcon,
  PlusIcon,
} from '../../icons/icons';
import TextBoxGroup from '../../text-box-group/text-box-group';
import SwitchGroup from '../../switch-group/switch-group';
import Checkbox from '../../custom-checkbox/custom-checkbox';
import Button from '../../button/button';
// import TitleEditable2 from '../title-editable2';
import TextBox from '../../text-box/text-box';
import { SEARCH_CURRENCY } from '../../../graphQL/supplierQuery';
import Action from '../../action/action';
import { getBach, handlePrdPkgChange, isGrossWtInvalid } from './buyerHelper';

function ProductDetailsTab({
  setSupplierTab,
  prodPackDetails,
  setProdPackDetails,
  products,
  setProducts,
  setTabDetails,
  packages,
  setPackages,
}) {
  const [t] = useTranslation('manageorders');
  const [currencyOptions, setCurrencyOptions] = useState([]);

  const { loading } = useQuery(SEARCH_CURRENCY, {
    onCompleted: (data) => {
      setCurrencyOptions(
        data?.fetchCurrency?.map((currency) => ({
          label: currency?.CurrencyCode,
          value: currency?.CurrencyCode,
        }))
      );
    },
  });

  const handleAddAnotherProduct = useCallback(() => {
    let newId = 1;
    if (products?.length) {
      newId = products[products.length - 1].id + 1 || 1;
    }
    const newProduct = {
      id: newId,
      name: '',
      quantity: null,
      length: null,
      width: null,
      height: null,
      grossWeight: null,
      pricePerUnit: null,
      currency: 'USD',
      measurementUnit: 'metric',
      customClearance: false,
      hazmat: false,
    };
    setProducts((prev) => [...prev, newProduct]);
  }, [products, setProducts]);

  const currencySelectOptions = useMemo(() => {
    return currencyOptions?.map((val) => (
      <option key={val?.value} value={val?.value}>
        {val?.label}
      </option>
    ));
  }, [currencyOptions]);
  const validateProdPackDetails = () => {
    const errors = {};
    // use if required
    if (!prodPackDetails.kindOfGoods) {
      errors.kindOfGoods = 'Kind of Goods is required.';
    }
    if (!prodPackDetails.cargoReadyDate) {
      errors.cargoReadyDate = 'Cargo Ready Date is required.';
    }
    if (prodPackDetails.customClearance && !prodPackDetails.HSCode?.trim()) {
      errors.HSCode = 'HS Code is required when Custom Clearance is true.';
    }
    if (prodPackDetails.hazmat && !prodPackDetails.UNCode?.trim()) {
      errors.UNCode = 'UN Code is required when Hazmat is true.';
    }
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  const validateProducts = () => {
    const isProductValid = (product) => {
      const {
        name,
        quantity,
        length,
        width,
        height,
        grossWeight,
        pricePerUnit,
        currency,
        measurementUnit,
      } = product;
      return (
        name?.trim() &&
        quantity &&
        length &&
        width &&
        height &&
        grossWeight &&
        pricePerUnit &&
        currency &&
        measurementUnit
      );
    };

    const isValid = products?.every(isProductValid);
    return {
      isValid,
      errorMessage: isValid
        ? null
        : 'At least one product must have all fields filled.',
    };
  };

  useEffect(() => {
    const isGrossWtValid = packages?.every(
      (pkg) => !isGrossWtInvalid(pkg, products)
    );

    setTabDetails((prev) => {
      const updatedTabDetails = { ...prev };
      if (validateProdPackDetails()?.isValid && validateProducts()?.isValid) {
        updatedTabDetails.disablePkgDetails = false;
      } else {
        updatedTabDetails.disablePkgDetails = true;
        updatedTabDetails.disableShipDetails = true;
        updatedTabDetails.disableNxtShip = true;
      }
      if (!isGrossWtValid) {
        updatedTabDetails.disableShipDetails = true;
        updatedTabDetails.disableNxtShip = true;
      }
      return updatedTabDetails;
    });
  }, [
    validateProdPackDetails()?.isValid,
    validateProducts()?.isValid,
    products,
  ]);

  const deleteProduct = (id) => {
    if (products?.length > 1) {
      setProducts((prev) => {
        const updatedProducts = prev?.filter((prd) => {
          if (prd?.id !== id) {
            return true;
          }
          return false;
        });
        return updatedProducts;
      });
      setPackages((prev) => {
        return prev?.map((pkg) => {
          if (pkg?.selectedProductIds?.includes(id)) {
            const updatedIds = pkg?.selectedProductIds?.filter(
              (prdId) => id !== prdId
            );
            return { ...pkg, selectedProductIds: updatedIds };
          }
          return pkg;
        });
      });
    }
  };

  return (
    <div className="supplier-booking-content ask-qut">
      <h3>{t('manageordersscreen.productdetails.goodsinformation')}</h3>
      <div className="d-flex w-50">
        <AdvanceSelectLabel
          id="kindOfGoods"
          label={t('manageordersscreen.productdetails.kindofgoods')}
          placeholder="Select"
          required
          data={[
            { label: 'General Cargo', value: 'General Cargo' },
            { label: 'Racks', value: 'Racks', disabled: true },
          ]}
          defaultValue={{
            label: prodPackDetails?.kindOfGoods,
            value: prodPackDetails?.kindOfGoods,
          }}
          removeSelectedValue={() => {
            setProdPackDetails((prev) => ({
              ...prev,
              kindOfGoods: '',
            }));
          }}
          className="mr-20 w-100"
          onChange={(e) =>
            setProdPackDetails((prev) => ({
              ...prev,
              kindOfGoods: e?.target?.value,
            }))
          }
        />
        <DatepickerBox
          required
          label={t('manageordersscreen.productdetails.cargoreadydate')}
          placeholder={t(
            'manageordersscreen.productdetails.cargoreadyplaceholder'
          )}
          setDate={(e) => {
            // if (e <= new Date()) {
            setProdPackDetails((prev) => ({ ...prev, cargoReadyDate: e }));
            // }
          }}
          selectedDate={prodPackDetails?.cargoReadyDate}
          // disableIcon={false}
          // enableEdit={true}
        />
      </div>

      <div className="package-details-outerwrapper">
        <div className="d-flex justify-between">
          <h3 className="m-0">
            {t('manageordersscreen.productdetails.createproducts')}
          </h3>
          {false && (
            <span className="card-icon">
              <BorderedBoxIcon />
            </span>
          )}
        </div>
        <div className="d-flex cc-and-hazmat-row">
          <Checkbox
            name="customClearance"
            value="Custom Clearance"
            label={t('manageordersscreen.productdetails.customclearance')}
            onChange={(e) => {
              setProdPackDetails((prev) => ({
                ...prev,
                customClearance: e?.target?.checked,
              }));
            }}
            checked={prodPackDetails?.customClearance}
          />
          {prodPackDetails?.customClearance && (
            <input
              id="hsCode"
              name="HSCode"
              type="text"
              placeholder={`${t(
                'manageordersscreen.productdetails.hscodeplaceholder'
              )}*`}
              onChange={({ target: { value } }) => {
                // Allow only numeric characters and trim to 10 characters
                if (/^\d*$/.test(value) && value.length <= 10) {
                  setProdPackDetails((prev) => ({
                    ...prev,
                    HSCode: value,
                  }));
                }
              }}
              value={prodPackDetails?.HSCode}
              pattern="\d{1,10}"
              required
            />
          )}
          <Checkbox
            name="hazmat"
            value="HAZMAT"
            label={t('manageordersscreen.productdetails.hazmat')}
            onChange={(e) => {
              setProdPackDetails((prev) => ({
                ...prev,
                hazmat: e?.target?.checked,
              }));
            }}
            checked={prodPackDetails?.hazmat}
          />
          {prodPackDetails?.hazmat && (
            <input
              type="text"
              id="unCode"
              name="UNCode"
              placeholder={`${t(
                'manageordersscreen.productdetails.uncodeplaceholder'
              )}*`}
              onChange={({ target: { value } }) => {
                // Allow only alphanumeric characters and trim to 10 characters
                if (/^[a-zA-Z0-9]*$/.test(value) && value.length <= 10) {
                  setProdPackDetails((prev) => ({
                    ...prev,
                    UNCode: value,
                  }));
                }
              }}
              value={prodPackDetails?.UNCode}
              pattern="[a-zA-Z0-9]{1,10}"
              required
            />
          )}
        </div>

        {products?.map((product) => (
          <div className="package-details-wrapper">
            <div className="package-details-card multiple">
              <div className="heading">
                <div className="left">
                  <span className="card-icon">
                    <BorderedBoxIcon />
                  </span>
                  <input
                    name="name"
                    value={product?.name}
                    className="mb-0"
                    onChange={(e) => {
                      e.target.name = 'name';
                      handlePrdPkgChange(e, product?.id, setProducts);
                    }}
                    placeholder={t(
                      'manageordersscreen.productdetails.productname'
                    )}
                    errorText={t(
                      'manageordersscreen.productdetails.noproducterror'
                    )}
                  />
                  <SwitchGroup
                    name="measurementUnit"
                    defaultSelected={0}
                    onChange={(e) => {
                      handlePrdPkgChange(e, product?.id, setProducts);
                    }}
                    disabled={!product?.name}
                  >
                    <span
                      className={
                        product?.measurementUnit === 'metric' ? '' : 'inactive'
                      }
                    >
                      {t('manageordersscreen.productdetails.metric')}
                    </span>
                    <span
                      className={
                        product?.measurementUnit === 'imperial'
                          ? ''
                          : 'inactive'
                      }
                    >
                      {t('manageordersscreen.productdetails.imperial')}
                    </span>
                  </SwitchGroup>
                </div>
                {products.length > 1 && (
                  <div className="right">
                    <Button
                      styles="icon"
                      icon={<CloseIcon />}
                      onClick={() => deleteProduct(product?.id)}
                    />
                  </div>
                )}
              </div>
              <div className="body">
                <table>
                  <thead>
                    <tr>
                      {[
                        'quantity',
                        'length',
                        'width',
                        'height',
                        'grossWeight',
                        'pricePerUnit',
                      ].map((field) => (
                        <th>
                          {t(`manageordersscreen.productdetails.${field}`)}
                          <span className="required">*</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {[
                        'quantity',
                        'length',
                        'width',
                        'height',
                        'grossWeight',
                      ].map((field) => (
                        <td key={field}>
                          <TextBoxGroup
                            id={field}
                            name={field}
                            type="number"
                            placeholder={t(
                              `manageordersscreen.productdetails.${field}`
                            )}
                            bach={getBach(product, field)}
                            onChange={(e) =>
                              handlePrdPkgChange(e, product?.id, setProducts)
                            }
                            // to maintain validation on pasting text
                            onPaste={(e) => {
                              e.preventDefault(); // Prevent the default paste action
                              const pastedValue =
                                e.clipboardData.getData('text');
                              if (/^\d*\.?\d*$/.test(pastedValue)) {
                                const [integerPart, decimalPart] =
                                  pastedValue.split('.');
                                const sanitizedValue = decimalPart
                                  ? `${integerPart}.${decimalPart.slice(
                                      0,
                                      field === 'quantity' ? 3 : 2
                                    )}`
                                  : integerPart;
                                // e.target.value =
                                //   field === 'quantity'
                                //     ? integerPart
                                //     : sanitizedValue;
                                e.target.value = sanitizedValue;
                                e.target.dispatchEvent(
                                  new Event('input', { bubbles: true })
                                ); // Trigger React change
                              }
                              handlePrdPkgChange(e, product?.id, setProducts);
                            }}
                            disabled={!product?.name}
                            value={product?.[field] || ''}
                            decimalLimit={field === 'quantity' ? 3 : 2}
                          />
                        </td>
                      ))}

                      <td>
                        {false && (
                          <Action
                            name="pricePerUnit"
                            label="Price Per Unit"
                            className="select-box"
                          >
                            <Checkbox value="bsjdnf" label="sbcnn" checked />
                          </Action>
                        )}
                        <div className="d-flex align-start price-group w-150">
                          <TextBox
                            id="pricePerUnit"
                            name="pricePerUnit"
                            // type="number"
                            min={0}
                            required
                            placeholder={t(
                              'manageordersscreen.productdetails.priceplaceholder'
                            )}
                            className="quotation"
                            defaultValue={product?.pricePerUnit || ''}
                            onChange={(e) =>
                              handlePrdPkgChange(e, product?.id, setProducts)
                            }
                            disabled={!product?.name}
                            // to maintain validation on pasting text
                            onPaste={(e) => {
                              e.preventDefault(); // Prevent the default paste action
                              const pastedValue =
                                e.clipboardData.getData('text');
                              if (/^\d*\.?\d*$/.test(pastedValue)) {
                                const [integerPart, decimalPart] =
                                  pastedValue.split('.');
                                const sanitizedValue = decimalPart
                                  ? `${integerPart}.${decimalPart.slice(0, 4)}`
                                  : integerPart;
                                e.target.value = sanitizedValue; // Set sanitized value
                                e.target.dispatchEvent(
                                  new Event('input', { bubbles: true })
                                ); // Trigger React change
                              }
                              handlePrdPkgChange(e, product?.id, setProducts);
                            }}
                          />
                          {!loading && (
                            <select
                              className="mt-0"
                              name="currency"
                              onChange={(e) =>
                                handlePrdPkgChange(
                                  {
                                    target: {
                                      name: 'currency',
                                      value: e.target.value,
                                    },
                                  },
                                  product?.id,
                                  setProducts
                                )
                              }
                              value={product?.currency || 'USD'}
                            >
                              {currencySelectOptions}
                            </select>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}

        <div className="supplier-booking-button-area justify-between">
          <button
            type="button"
            className="add-more"
            onClick={handleAddAnotherProduct}
          >
            <PlusIcon color="#0088fa" />{' '}
            {t('manageordersscreen.productdetails.addanotherproduct')}
          </button>
        </div>
      </div>

      <div className="supplier-booking-button-area mt-10">
        <Button
          label={t('manageordersscreen.createbookingmenu.confirmentities.next')}
          styles="submit"
          size="large"
          iconRight={<NextIcon />}
          onClick={() => {
            setTabDetails((prev) => {
              const updatedTabDetails = { ...prev };
              updatedTabDetails.productDetails = true;
              return updatedTabDetails;
            });
            setSupplierTab('packaging details');
          }}
          spacing="0.694vw"
          className="px-24"
          disabled={
            !validateProdPackDetails()?.isValid || !validateProducts()?.isValid
          }
        />
      </div>
    </div>
  );
}

export default ProductDetailsTab;

ProductDetailsTab.propTypes = {
  setSupplierTab: PropTypes.func,
  prodPackDetails: PropTypes.instanceOf(Object),
  setProdPackDetails: PropTypes.func,
  products: PropTypes.instanceOf(Array),
  setProducts: PropTypes.func,
  setTabDetails: PropTypes.func,
  packages: PropTypes.instanceOf(Array),
  setPackages: PropTypes.func,
};

ProductDetailsTab.defaultProps = {
  setSupplierTab: () => {},
  prodPackDetails: {},
  setProdPackDetails: () => {},
  products: [],
  setProducts: () => {},
  setTabDetails: () => {},
  packages: [],
  setPackages: () => {},
};
